import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Icon — canonical glyph dictionary for every Sparta portal (SCRUM-199).
 *
 * Backed by Lucide (`lucide-react`, ISC) for professionally-drawn, visually
 * consistent glyphs — replacing the hand-sketched paths that caused the
 * distortion/drift the SCRUM-58 audit flagged.
 *
 * `REGISTRY` below is the SINGLE SOURCE OF TRUTH: `IconName` is derived from
 * it (`keyof typeof REGISTRY`) and `ALL_ICON_NAMES` is exported so the spec
 * and Storybook consume one runtime list — the catalog can't drift across
 * three files.
 *
 * Visual contract preserved from the prior hand-rolled version: 24×24
 * viewBox, default size 20, strokeWidth 1.75, `stroke="currentColor"`,
 * `fill="none"`, `aria-hidden` + `focusable={false}` (decorative). Lucide
 * renders those defaults; the wrapper applies the kit's size/stroke + a11y
 * attrs.
 *
 * `portal` is the one retained custom glyph (the portal-chooser brand mark);
 * it renders through the same prop contract as the Lucide glyphs so the
 * registry stays uniform.
 *
 * Adding a glyph: import the Lucide component and add one `name: Component`
 * entry to `REGISTRY`. The type, the runtime list, the Storybook gallery and
 * the spec loop all follow automatically.
 *
 * Bundle note: this is a CENTRAL registry — importing `Icon` retains every
 * registered glyph it can dispatch to. Named Lucide imports keep us from
 * pulling in the whole library, but each registered name is shared runtime
 * weight, so keep the registry lean (add on real need).
 */
import * as React from 'react';
import { House, Inbox, Building2, Zap, Info, LineChart, Calendar, Users, Check, CheckCheck, Plus, Minus, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, ChevronsUpDown, Sun, Moon, Monitor, Bell, Search, Menu, X, LayoutGrid, List, Settings, Sparkles, Sparkle, Stars, ArrowRight, ArrowUp, ArrowDown, ArrowLeftRight, SlidersHorizontal, Palette, Key, KeyRound, User, Mail, Lock, Shield, Eye, MoreHorizontal, MoreVertical, Filter, RefreshCw, GripVertical, Star, Bed, Clock, Map, Receipt, Pencil, Laptop, Tablet, Smartphone, MessageSquare, LogOut, Trash2, Download, Upload, ExternalLink, AlertTriangle, CheckCircle2, XCircle, Copy, Link, Loader, 
// amenity categories (SCRUM-141)
Bath, Tv, Baby, Thermometer, Wifi, Utensils, MapPin, Trees, Car, Accessibility, } from 'lucide-react';
/**
 * Retained custom glyph (SCRUM-199 D1): the portal-chooser brand mark
 * (4-square). Renders through the same `LucideProps` contract as the Lucide
 * glyphs so it can live in the same registry and honour `size`/`strokeWidth`.
 */
const PortalGlyph = React.forwardRef(function PortalGlyph({ size = 24, strokeWidth = 2, ...rest }, ref) {
    return (_jsxs("svg", { ref: ref, width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: strokeWidth, strokeLinecap: "round", strokeLinejoin: "round", ...rest, children: [_jsx("rect", { x: "4", y: "4", width: "7", height: "7", rx: "1" }), _jsx("rect", { x: "13", y: "4", width: "7", height: "7", rx: "1" }), _jsx("rect", { x: "4", y: "13", width: "7", height: "7", rx: "1" }), _jsx("rect", { x: "13", y: "13", width: "7", height: "7", rx: "1" })] }));
});
/**
 * name → glyph component. SINGLE SOURCE OF TRUTH. `satisfies` keeps the
 * literal keys (so `IconName` derives correctly) while checking every value
 * is a `LucideIcon`-compatible component.
 */
const REGISTRY = {
    // navigation / structure
    home: House,
    home2: House, // legacy alias → House (D4)
    inbox: Inbox,
    building: Building2,
    grid: LayoutGrid,
    list: List,
    portal: PortalGlyph, // retained custom (brand)
    menu: Menu,
    // chevrons / arrows
    chevDown: ChevronDown,
    chevUp: ChevronUp,
    chevLeft: ChevronLeft,
    chevRight: ChevronRight,
    chevronsUpDown: ChevronsUpDown,
    arrowRight: ArrowRight,
    arrowUp: ArrowUp,
    arrowDown: ArrowDown,
    swap: ArrowLeftRight, // D1: was custom → Lucide
    // status / feedback
    check: Check,
    checks: CheckCheck,
    checkCircle2: CheckCircle2,
    xCircle: XCircle,
    alertTriangle: AlertTriangle,
    info: Info,
    loader: Loader,
    plus: Plus,
    minus: Minus,
    close: X,
    x: X, // alias of close
    // actions
    settings: Settings,
    sliders: SlidersHorizontal,
    filter: Filter,
    refresh: RefreshCw,
    search: Search,
    pencil: Pencil,
    trash2: Trash2,
    copy: Copy,
    link: Link,
    externalLink: ExternalLink,
    download: Download,
    upload: Upload,
    drag: GripVertical,
    more: MoreHorizontal,
    moreH: MoreHorizontal, // alias of more
    moreV: MoreVertical,
    // identity / security
    user: User,
    users: Users,
    key: Key,
    keyfilled: KeyRound, // D2: stroke-only kit → KeyRound
    lock: Lock,
    shield: Shield, // D1: was custom → Lucide
    logout: LogOut,
    // comms
    mail: Mail,
    bell: Bell,
    msg: MessageSquare,
    // content / media
    eye: Eye,
    palette: Palette,
    star: Star,
    sparkles: Sparkles, // D3
    spark: Sparkle, // D3
    sparkle2: Stars, // D3
    bolt: Zap,
    // data / domain
    chart: LineChart,
    calendar: Calendar,
    clock: Clock,
    map: Map,
    receipt: Receipt,
    bed: Bed,
    // devices / theming
    sun: Sun,
    moon: Moon,
    monitor: Monitor,
    laptop: Laptop,
    tablet: Tablet,
    phone: Smartphone,
    // amenity categories (SCRUM-141)
    bath: Bath,
    tv: Tv,
    baby: Baby,
    thermometer: Thermometer,
    wifi: Wifi,
    utensils: Utensils,
    mapPin: MapPin,
    trees: Trees,
    car: Car,
    accessibility: Accessibility,
};
/**
 * Runtime list of every registered name — the single source consumed by
 * `Icon.stories.tsx` and `Icon.spec.tsx` so the catalog lives in one place.
 */
export const ALL_ICON_NAMES = Object.keys(REGISTRY);
export function Icon({ name, size = 20, strokeWidth = 1.75, ...rest }) {
    // Collapse the per-name component union to the uniform `LucideIcon` type
    // (every registry value satisfies it) so the JSX call is well-typed.
    const Glyph = REGISTRY[name];
    return (_jsx(Glyph, { size: size, strokeWidth: strokeWidth, "aria-hidden": true, focusable: false, ...rest }));
}
//# sourceMappingURL=Icon.js.map